import { Insight, InsightSeverity, Sentiment } from '../../../common/hooks/use-insights-api.hook';

/**
 * How a finding looks and reads.
 *
 * Kept out of the components so the palette and the wording rules are in one
 * place and can be asserted in tests — the severity colours in particular are
 * load-bearing: red is reserved for findings that are costing the vendor
 * something, and if everything shouts then nothing does.
 */

/**
 * Severity says how urgent. Sentiment says whether it is welcome. The card is
 * styled by BOTH, because they answer different questions and a vendor reads
 * the colour before the words.
 *
 * The case that forces this: a fall in empty searches is a fall and a win at
 * the same time. Styling on severity alone gave good news the same neutral blue
 * as "nothing much happened", so nobody could tell at a glance which numbers
 * were going their way.
 */
export interface SeverityStyle {
    /** Tailwind classes for the severity badge. */
    badge: string;
    /** Tailwind classes for the dot inside the badge. */
    dot: string;
    labelKey: string;
}

const CRITICAL: SeverityStyle = {
    badge: 'border-destructive/30 bg-destructive/10 text-destructive',
    dot: 'bg-destructive',
    labelKey: 'INSIGHTS.SEVERITY.CRITICAL',
};

const ATTENTION: SeverityStyle = {
    badge: 'border-warning/30 bg-warning/10 text-warning',
    dot: 'bg-warning',
    labelKey: 'INSIGHTS.SEVERITY.WARNING',
};

/** Good news earns green. Nothing else in the queue does. */
const GOOD: SeverityStyle = {
    badge: 'border-success/30 bg-success/10 text-success',
    dot: 'bg-success',
    labelKey: 'INSIGHTS.SEVERITY.GOOD',
};

/** Neutral is stone, not blue: the chrome has no colour of its own. */
const NEUTRAL: SeverityStyle = {
    badge: 'border-border bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground',
    labelKey: 'INSIGHTS.SEVERITY.INFO',
};

/** Kept for tests and any caller that only knows a severity. */
export const SEVERITY: Record<InsightSeverity, SeverityStyle> = {
    critical: CRITICAL,
    warning: ATTENTION,
    info: NEUTRAL,
};

export const severityStyle = (severity: InsightSeverity, sentiment: Sentiment = 'neutral'): SeverityStyle => {
    // Bad news keeps its urgency colouring; good news is green whatever its
    // severity; anything undeclared stays neutral rather than being guessed at.
    if (sentiment === 'good') return GOOD;
    if (sentiment === 'bad') return severity === 'critical' ? CRITICAL : ATTENTION;
    return NEUTRAL;
};

/**
 * The verdict's surface — the panel it sits on, not the words.
 *
 * The tone used to colour the sentence itself, which made a whole paragraph of
 * red the loudest thing on the dashboard and pulled the eye away from the
 * findings it was introducing. The sentence reads as text now; the tone is a
 * wash behind it and a dot beside it, which is enough to know at a glance
 * whether the news is good without being shouted at.
 */
export const VERDICT_TONE: Record<Sentiment, string> = {
    bad: 'border-destructive/20 bg-destructive/[0.04]',
    good: 'border-success/20 bg-success/[0.04]',
    neutral: 'border-border bg-muted/40',
};

/** The tone, at the one size that can carry colour without dominating. */
export const VERDICT_DOT: Record<Sentiment, string> = {
    bad: 'bg-destructive',
    good: 'bg-success',
    neutral: 'bg-muted-foreground',
};

/**
 * The label on a finding's action button.
 *
 * Every `suggestedAction.kind` the engine can emit is mapped here. An unknown
 * kind falls back to a neutral label rather than rendering the raw slug — a
 * vendor should never be shown `inspect_funnel_step`.
 */
const ACTION_LABELS: Record<string, string> = {
    inspect_metric: 'INSIGHTS.ACTION.INSPECT_METRIC',
    inspect_funnel_step: 'INSIGHTS.ACTION.INSPECT_FUNNEL',
    fix_instrumentation: 'INSIGHTS.ACTION.FIX_INSTRUMENTATION',
    enable_metric: 'INSIGHTS.ACTION.ENABLE_METRIC',
    generate_content: 'INSIGHTS.ACTION.GENERATE_CONTENT',
    connect_website: 'INSIGHTS.ACTION.CONNECT_WEBSITE',
    connect_social: 'INSIGHTS.ACTION.CONNECT_SOCIAL',
    read_brand: 'INSIGHTS.ACTION.READ_BRAND',
    plan_posts: 'INSIGHTS.ACTION.PLAN_POSTS',
    add_competitors: 'INSIGHTS.ACTION.ADD_COMPETITORS',
    embed_visualizer: 'INSIGHTS.ACTION.EMBED_VISUALIZER',
    run_analysis: 'INSIGHTS.ACTION.RUN_ANALYSIS',
    fix_catalog: 'INSIGHTS.ACTION.FIX_CATALOG',
    categorise_products: 'INSIGHTS.ACTION.CATEGORISE_PRODUCTS',
    improve_page: 'INSIGHTS.ACTION.IMPROVE_PAGE',
};

/**
 * The one action that starts work rather than opening a page: the business
 * analyst. The card creates the run and sends the vendor to Work to watch it.
 */
export const startsAnalysis = (finding: Insight): boolean => finding.suggestedAction?.kind === 'run_analysis';

/**
 * Filing the products that were added after the catalogue was organised.
 *
 * Like the analyst, this starts a run rather than opening a page — the card
 * already says what needs doing and how much of it there is, so making the
 * vendor go to Categories and find the button again is asking them to agree
 * twice. It carries `onlyUncategorised` so the run places the stragglers into
 * the tree that exists instead of re-reading and re-arranging the catalogue.
 */
export const startsCategorisation = (finding: Insight): boolean =>
    finding.suggestedAction?.kind === 'categorise_products';

/**
 * A setup gap the card can close in place.
 *
 * Asking for a website is one field; sending the vendor to a settings page
 * to type it is a detour that most will not take. These actions render an
 * input on the card itself and write to the vendor profile; the server then
 * recomputes the gaps, so the card leaves on its own.
 */
export type InlineField = 'websiteUrl' | 'competitors';

/**
 * A finding's words in the vendor's language, falling back to the server's.
 *
 * Findings are written by a scheduled sweep and read later by whoever opens
 * the page — possibly two colleagues reading in two languages — so the words
 * cannot be chosen when the finding is written. The server sends keys and the
 * numbers they need; this picks them up.
 *
 * The English stays the fallback rather than the source: a finding written
 * before this existed, or one written by a model in prose, has no key and must
 * still read as a sentence.
 */
export function findingText(
    t: (key: string, opts?: Record<string, unknown>) => string,
    fallback: string | undefined,
    keyed?: { key?: string; params?: Record<string, string | number> },
): string {
    if (!keyed?.key) return fallback ?? '';

    const params: Record<string, unknown> = { ...(keyed.params ?? {}) };

    /*
     * A metric's name is itself a translatable phrase, and it sits inside the
     * sentence. Resolved first, so a French vendor reads a French metric name
     * in a French sentence rather than one of each. Its English stays the
     * fallback, as everywhere else here.
     */
    if (typeof params.metricKey === 'string') {
        params.metric = t(params.metricKey, { defaultValue: String(params.metric ?? '') });
    }

    /*
     * "up" and "down" are words too, and they sit mid-sentence. English gets
     * away with one word for both readings; French wants "en hausse de", which
     * is why the direction is a key rather than an arrow.
     */
    /* Same reasoning as the metric name: a noun inside the sentence. */
    if (typeof params.nounKey === 'string') {
        params.noun = t(params.nounKey, { defaultValue: String(params.noun ?? '') });
    }

    if (params.direction === 'up' || params.direction === 'down') {
        params.direction = t(`INSIGHTS.FINDING.DIRECTION.${params.direction.toUpperCase()}`, {
            defaultValue: params.direction,
        });
    }

    return t(keyed.key, { ...params, defaultValue: fallback ?? '' });
}

/**
 * The verdict, in the vendor's language.
 *
 * Its bad case quotes the worst finding's title, so that finding is translated
 * first and dropped into the sentence — otherwise a French verdict would carry
 * an English headline inside it, which is how the whole problem looked before.
 */
export function verdictText(
    t: (key: string, opts?: Record<string, unknown>) => string,
    verdict: { text: string; i18n?: { key: string; params?: Record<string, string | number>; lead?: { title?: string; params?: Record<string, string | number> }; leadText?: string } },
): string {
    if (!verdict.i18n?.key) return verdict.text;

    const headline = findingText(t, verdict.i18n.leadText, {
        key: verdict.i18n.lead?.title,
        params: verdict.i18n.lead?.params,
    }).replace(/\.$/, '');

    return t(verdict.i18n.key, { ...(verdict.i18n.params ?? {}), headline, defaultValue: verdict.text });
}

/** Keyed on the action, so the setup ladder and the queue agree without sharing a shape. */
export const inlineFieldForAction = (kind?: string): InlineField | null => {
    switch (kind) {
        case 'connect_website':
            return 'websiteUrl';
        case 'add_competitors':
            return 'competitors';
        default:
            return null;
    }
};

export const inlineField = (finding: Insight): InlineField | null =>
    inlineFieldForAction(finding.suggestedAction?.kind);

/**
 * The brief a `generate_content` finding hands to Content Studio.
 *
 * Written here rather than on the server because it is copy, not data: the
 * server sends the product and the number, and the vendor's own language
 * turns that into a sentence. Sending an English string from a detector would
 * have arrived untranslated in front of a French vendor.
 */
export const contentBrief = (
    finding: Insight,
    t: (key: string, fallback: string, opts?: Record<string, unknown>) => string,
): string => {
    const params = (finding.suggestedAction?.params ?? {}) as { productName?: string; engagements?: number };
    const product = params.productName ?? '';

    return t(
        'INSIGHTS.BRIEF.GENERATE_CONTENT',
        'Create images for {{product}}. It drew {{count}} interactions recently and has no Content Studio images yet.',
        { product, count: params.engagements ?? 0 },
    );
};

export const actionLabelKey = (finding: Insight): string =>
    (finding.suggestedAction && ACTION_LABELS[finding.suggestedAction.kind]) || 'INSIGHTS.ACTION.DEFAULT';

/**
 * Where a finding's action goes.
 *
 * Analytics is the place to explore a number; the queue is only for deciding
 * that a number is worth exploring. Instrumentation findings are aimed at the
 * team rather than the vendor and have nowhere to send anyone, so they get no
 * destination and the card shows a dismiss instead.
 */
export const actionRoute = (finding: Insight): string | null =>
    routeForAction(finding.suggestedAction?.kind, finding.suggestedAction?.params);

/**
 * Keyed on the action, so the setup ladder and the queue send a vendor to the
 * same place. They ask for the same seven things; two maps meant two answers,
 * and the second one written was wrong about three of them.
 */
export const routeForAction = (kind?: string, rawParams?: Record<string, unknown>): string | null => {
    switch (kind) {
        // The run is started by the card; this is where it goes to watch it.
        case 'categorise_products':
            return '/products/categories';
        case 'inspect_metric':
        case 'inspect_funnel_step':
            return '/visualizer/engagement/analytics';
        /**
         * The first action that is work rather than reading.
         *
         * Everything above sends the vendor to a chart to decide something.
         * This sends them to the studio with the brief already written, so the
         * finding ends in a picture instead of another decision.
         *
         * The product travels in the URL rather than in router state so the
         * link survives a refresh, a new tab, and being pasted to a colleague.
         */
        case 'generate_content': {
            const params = (rawParams ?? {}) as { productUuid?: string };
            const query = params.productUuid ? `?product=${encodeURIComponent(params.productUuid)}` : '';
            return `/marketing/content-studio${query}`;
        }
        /**
         * Setup gaps go where the thing gets set up. The two that are a single
         * field (`connect_website`, `add_competitors`) have no route — they are
         * answered on the card, see `inlineField`.
         */
        case 'read_brand':
            return '/marketing/brand';
        case 'connect_social':
            return '/marketing/social-scheduler?tab=accounts';
        case 'plan_posts':
            return '/marketing/social-scheduler';
        case 'embed_visualizer':
            return '/home/integration';
        case 'fix_catalog':
            return '/products/catalog';
        case 'connect_shop':
            return '/business/sales/shop';
        /** The analyst's "improve this page" points at the vendor's own site. */
        case 'improve_page': {
            const url = String((rawParams as { url?: string } | undefined)?.url ?? '');
            return /^https?:\/\//.test(url) ? url : '/marketing/brand';
        }
        /**
         * Handled by the card: it starts a run and stays put. Work is no
         * longer a route — the dashboard's AI section shows the run — and
         * sending someone away from the card they just acted on was never
         * much use anyway.
         */
        case 'run_analysis':
            return '/';
        default:
            return null;
    }
};

/**
 * "Just now", "2 hours ago", "3 days ago".
 *
 * Deliberately coarse. A finding is produced by a scheduled sweep, so a
 * to-the-minute timestamp would imply a precision the data does not have.
 */
export function relativeTime(iso: string, now: Date = new Date()): { key: string; count: number } {
    const then = new Date(iso).getTime();
    const minutes = Math.max(0, Math.round((now.getTime() - then) / 60_000));

    if (minutes < 60) return { key: 'INSIGHTS.TIME.JUST_NOW', count: 0 };

    const hours = Math.round(minutes / 60);
    if (hours < 24) return { key: 'INSIGHTS.TIME.HOURS', count: hours };

    return { key: 'INSIGHTS.TIME.DAYS', count: Math.round(hours / 24) };
}

/**
 * The provenance line under the header.
 *
 * The reference design puts a confidence score here ("96% CONFIDENT based on
 * 1500 sessions"). We do not have a confidence model, and inventing one would
 * be exactly the plausible-looking number this whole layer exists to prevent.
 * What we can state truthfully is what was compared and how much was checked,
 * which fills the same slot and is auditable.
 */
export function provenance(items: Insight[], detectorCount: number): { days: number; checks: number } {
    const first = items[0];
    const days = first
        ? Math.max(
              1,
              Math.round(
                  (new Date(first.periodTo).getTime() - new Date(first.periodFrom).getTime()) / 86_400_000,
              ),
          )
        : 14;

    return { days, checks: detectorCount };
}
